import React from "react";

const EXPAND_ICON = "/icons/clinicians/expand-more.svg";

/**
 * The compact Institution list filter used by Clinicians, Patients and other
 * destination lists. It deliberately owns the familiar All-chip geometry
 * instead of falling back to a browser select control.
 */
export default function InstitutionStatusFilterChip({ value, options, onChange, ariaLabel }) {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef(null);
  const triggerRef = React.useRef(null);
  const selected = options.find((option) => option.value === value) || options[0];

  const close = React.useCallback(({ returnFocus = false } = {}) => {
    setOpen(false);
    if (returnFocus) window.requestAnimationFrame(() => triggerRef.current?.focus?.());
  }, []);

  React.useEffect(() => {
    if (!open) return undefined;
    const dismiss = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        close({ returnFocus: true });
      }
      if (event.type === "pointerdown" && !rootRef.current?.contains(event.target)) close({ returnFocus: true });
    };
    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("keydown", dismiss);
    return () => {
      document.removeEventListener("pointerdown", dismiss);
      document.removeEventListener("keydown", dismiss);
    };
  }, [close, open]);

  return (
    <div ref={rootRef} className="institution-clinician-filter-chip-wrap">
      <button
        ref={triggerRef}
        type="button"
        className="institution-clinician-filter-chip"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span>{selected?.label}</span>
        <img src={EXPAND_ICON} alt="" aria-hidden="true" className="h-[18px] w-[18px] object-contain" />
      </button>
      {open ? (
        <div className="institution-clinician-filter-menu" role="listbox" aria-label={ariaLabel}>
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={option.value === value}
              onClick={() => {
                onChange(option.value);
                close({ returnFocus: true });
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
